import { ArrowRightOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { useRequest } from 'ahooks';
import { AxiosError } from 'axios';
import ConversationTopBar from 'components/chats/ConversationTopBar';
import PatientDocumentsList from 'components/lists/PatientDocumentsList';
import PaywallModal from 'components/modals/PaywallModal';
import { Alert, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import useSelectConversations from 'hooks/useSelectConversations';
import { InfoIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiPatientDocumentsManager, { PatientDocument } from 'services/api/ApiPatientDocumentsManager';
import { toast } from 'sonner';
import { selectDoesUserHaveFullAccess } from 'store/users/selectors';

// Document validation error codes from the backend serializer
const DOCUMENT_VALIDATION_ERROR_CODES = ['INVALID_FILE_TYPE', 'FILE_TOO_LARGE', 'DAILY_UPLOAD_LIMIT_REACHED'] as const;

/**
 * Extracts error code from DRF error response.
 * DRF stores codes in ErrorDetail objects: {"field": [{"code": "...", "message": "..."}]}
 * or {"non_field_errors": [{"code": "...", "message": "..."}]}
 */
function extractErrorCode(errorData: any): string | undefined {
  if (!errorData || typeof errorData === 'string') {
    return undefined;
  }

  if (errorData.file && errorData.file.code) {
    // for errors related to the `file` field
    return errorData.file.code;
  }

  if (errorData.code && Array.isArray(errorData.code)) {
    // for generic errors; e.g. the one for the daily upload limit
    return errorData.code[0];
  }

  return undefined;
}

export default function PatientDocumentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasFullAccess = useSelector(selectDoesUserHaveFullAccess);
  const { conversationMetadata } = useSelectConversations();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentCaregiver = conversationMetadata?.conversation.current_owner;

  const {
    data: documents,
    loading,
    refresh,
  } = useRequest(() => ApiPatientDocumentsManager.list({}), {
    onError: error => {
      console.error('[PatientDocumentsPage] Error loading documents:', error);
      toast.error(t('documents.errors.loadError'));
    },
  });

  const { loading: uploading, run: uploadDocument } = useRequest(
    (file: File) =>
      ApiPatientDocumentsManager.upload({
        file,
        caregiverId: currentCaregiver?.id,
      }),
    {
      manual: true,
      onSuccess: () => {
        if (currentCaregiver) {
          toast.success(
            t('documents.uploadAndShareSuccess', {
              caregiverName: currentCaregiver.display_name,
            }),
          );
        } else {
          toast.success(t('documents.uploadSuccess'));
        }
        refresh();
      },
      onError: error => {
        console.error('[PatientDocumentsPage] Error uploading document:', error);
        let errorMessage = t('documents.errors.uploadError');

        if (error instanceof AxiosError) {
          const errorCode = extractErrorCode(error.response?.data);
          if (errorCode && DOCUMENT_VALIDATION_ERROR_CODES.includes(errorCode as any)) {
            const translatedError = t(`documents.errors.${errorCode}` as any);
            // Only use the translated error if it's not the same as the key (meaning translation exists)
            if (translatedError !== `documents.errors.${errorCode}`) {
              errorMessage = translatedError;
            }
          }
        }

        toast.error(errorMessage);
      },
    },
  );

  const handleUpload = async (file: File): Promise<boolean> => {
    posthog.capture('patient_documents.document_upload_started');
    // Client-side validation
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
    const maxSize = 30 * 1024 * 1024; // 30MB

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(t('documents.errors.invalidType'));
      return false;
    }

    if (file.size > maxSize) {
      toast.error(t('documents.errors.fileTooLarge'));
      return false;
    }

    // On Android/Capacitor, read the file into memory to prevent ERR_UPLOAD_FILE_CHANGED
    // This ensures we have a stable file reference
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: file.type });
        const stableFile = new File([blob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        uploadDocument(stableFile);
      } catch (error) {
        console.error('[PatientDocumentsPage] Error reading file:', error);
        toast.error(t('documents.errors.uploadError'));
        return false;
      }
    } else {
      uploadDocument(file);
    }
    return false; // Prevent default upload
  };

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleUpload(file);
    }
    // Reset so that picking the same file again fires `onChange`.
    event.target.value = '';
  };

  const isSharedWithCurrentCaregiver = (document: PatientDocument): boolean => {
    if (!currentCaregiver) return false;
    return document.shared_with_caregivers.includes(currentCaregiver.id);
  };

  // Split documents into shared and not shared
  const sharedDocuments = documents?.filter(doc => isSharedWithCurrentCaregiver(doc)) || [];
  const notSharedDocuments = documents?.filter(doc => !isSharedWithCurrentCaregiver(doc)) || [];

  const sectionClassName = 'mb-6 w-full max-w-content';
  const sectionTitleClassName = 'mb-3 text-base font-semibold text-foreground';
  const emptyStateClassName = 'p-10 text-center text-muted-foreground';

  return (
    <div className="flex h-full flex-col">
      <ConversationTopBar
        showBackButton
        avatar={conversationMetadata?.conversation.current_owner?.photo_url}
        name={conversationMetadata?.conversation.current_owner?.display_name}
        onBackButtonClick={() => navigate(-1)}
      />
      <div className="flex flex-1 flex-col items-center overflow-y-auto bg-background p-4">
        {loading && documents === undefined ? (
          <div className={emptyStateClassName}>{t('common.loading')}</div>
        ) : (
          <>
            {/* Shared Documents Section */}
            <div className={sectionClassName}>
              {(sharedDocuments.length > 0 || notSharedDocuments.length > 0) && (
                <div className={sectionTitleClassName}>
                  {currentCaregiver
                    ? t('documents.sharedWith', {
                        caregiverName: currentCaregiver.display_name,
                      })
                    : t('documents.shared')}
                </div>
              )}
              {sharedDocuments.length === 0 ? (
                <div className={emptyStateClassName}>{t('documents.noSharedDocuments')}</div>
              ) : (
                <PatientDocumentsList
                  documents={sharedDocuments}
                  isCaregiver={false}
                  currentCaregiver={currentCaregiver}
                  onRefresh={refresh}
                />
              )}
              {/* Information callout when documents exist but none are shared with current caregiver */}
              {currentCaregiver && documents && documents.length > 0 && sharedDocuments.length === 0 && (
                <Alert variant="info">
                  <AlertTitle className="line-clamp-none">
                    <InfoCircleOutlined className="mr-2" />
                    {t('documents.reshareInfo')}
                  </AlertTitle>
                </Alert>
              )}
              <div className="flex w-full items-center justify-center">
                {hasFullAccess ? (
                  <div className="w-full px-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      className="hidden"
                      disabled={uploading}
                      onChange={onFileInputChange}
                    />
                    <Button
                      variant="secondary"
                      size="lg"
                      loading={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      // A long caregiver name must wrap instead of pushing the
                      // page into horizontal scroll.
                      className="mt-4 mb-8 h-auto min-h-16 w-full whitespace-normal py-3 text-foreground"
                    >
                      <UploadOutlined />
                      {currentCaregiver
                        ? t('documents.uploadWithCaregiver', {
                            caregiverName: currentCaregiver.display_name,
                          })
                        : t('documents.upload')}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 mb-8 w-full bg-background p-2.5">
                    <Alert
                      variant="info"
                      className="cursor-pointer"
                      tabIndex={0}
                      onClick={() => {
                        setIsPaywallOpen(true);
                        posthog.capture('paywall.opened', { source: 'documents' });
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') setIsPaywallOpen(true);
                      }}
                    >
                      <InfoIcon aria-hidden />
                      <AlertTitle className="line-clamp-none">
                        {t('documents.paywall.uploadOnlyForPaidUsers')} <ArrowRightOutlined />
                      </AlertTitle>
                    </Alert>
                  </div>
                )}
              </div>
            </div>

            {/* Not Shared Documents Section */}
            {currentCaregiver && notSharedDocuments.length > 0 && (
              <div className={sectionClassName}>
                <div className={sectionTitleClassName}>{t('documents.notShared')}</div>
                <PatientDocumentsList
                  documents={notSharedDocuments}
                  isCaregiver={false}
                  currentCaregiver={currentCaregiver}
                  onRefresh={refresh}
                />
              </div>
            )}
          </>
        )}
      </div>
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        closeModal={() => setIsPaywallOpen(false)}
        suggestTestIfEligible={false}
      />
    </div>
  );
}
